import type { Role, User } from "@types";
import { canAccessRoute, findMatchingRoute } from "../../router/routeConfig";
import { currentUser, permissionsForUser } from "./permissions";

/**
 * Can a user with `role` + `permissions` reach `path`?
 *
 * The single gating predicate for nav projection: resolves `path` to its route
 * and defers to `canAccessRoute` — the SAME authority the route guard and loader
 * use. So a nav item is shown iff the guard would let the user open it; nav and
 * guard can't disagree. Unknown paths are treated as unreachable (fail-closed).
 */
export const canReachPath = (
  path: string,
  role: Role | undefined,
  permissions: Set<string>,
): boolean => {
  const route = findMatchingRoute(path);
  return !!route && canAccessRoute(route, role, permissions);
};

/**
 * Filter a list of nav items down to those the current (localStorage) user can
 * reach. `getPath` extracts the route path from each item (header items use
 * `href`, the coach manifest uses `path`). Reads the localStorage-canonical user
 * so the projection matches the guard exactly.
 */
export const filterReachableNav = <T>(
  items: readonly T[],
  getPath: (item: T) => string,
  user: User | undefined = currentUser(),
): T[] => {
  const role = user?.role;
  const permissions = permissionsForUser(user);
  return items.filter((item) => canReachPath(getPath(item), role, permissions));
};
